// js/core/production-outliers.js — is this day's production number believable?
//
// 2026-10-30: a foreman read "Veneer Brick" in big letters and "SF" in a 9px
// chip, counted bricks into a square-foot item for a week, and the job showed
// ~$155k of profit it never made. 4 of the 5 days beat the company's best day
// ever. Nothing said a word. Two answers live here, shared by the field form
// (js/app.js) and Job Costs (cost-warnings.js productionOutliers):
//   · the unit spelled out ("square feet — area, not pieces");
//   · one day held against the company's own history for that item.
// Pure: no DOM, no fetch, no i18n import — cost-warnings.js imports this and
// must load in plain Node (scripts/demo). Words come from the caller's `t`
// (window.Ordinex.i18n.t in the browser), keys in i18n/en.json.

/** Fewer past days than this for an item and nothing is judged. */
export const MIN_DAYS = 5;
/** A day above this many times the usual (median) day is asked about. */
export const MEDIAN_FACTOR = 3;

// The units a catalogue usually holds, matched loosely — the unit is free text
// (Rates & Defaults → Production Items). `dim` says what a measured unit
// measures; a counted unit has none. Anything else prints exactly as typed.
const UNITS = [
  ['sf',  /^(sf|sq\.?\s*ft\.?|sqft|ft2|ft²|square\s*f(ee|oo)t)$/i, 'area'],
  ['lf',  /^(lf|lin\.?\s*ft\.?|linear\s*f(ee|oo)t)$/i,            'length'],
  ['m2',  /^(m2|m²|sq\.?\s*m|sqm|square\s*met(re|er)s?)$/i,        'area'],
  ['lm',  /^(m|lm|lin\.?\s*m|linear\s*met(re|er)s?|met(re|er)s?)$/i, 'length'],
  ['cy',  /^(cy|cu\.?\s*yds?|yd3|yd³|cubic\s*yards?)$/i,           'volume'],
  ['m3',  /^(m3|m³|cu\.?\s*m|cubic\s*met(re|er)s?)$/i,             'volume'],
  ['pcs', /^(count|ea|each|pc|pcs|pieces?|units?)?$/i,             null],
  ['hr',  /^(h|hr|hrs|hours?)$/i,                                  null],
];

/**
 * A catalogue unit in words: { short, name, dim }.
 *   'sf'    → { short: 'sq ft', name: 'square feet', dim: 'area' }
 *   'count' → { short: 'pcs',   name: 'pieces',      dim: null }
 *   'bags'  → { short: 'bags',  name: 'bags',        dim: null }   (as typed)
 */
export function unitWords(unit, t) {
  const u = String(unit ?? '').trim();
  const hit = UNITS.find(([, re]) => re.test(u));
  if (!hit) return { short: u, name: u, dim: null };
  const [kind, , dim] = hit;
  return { short: t('prodUnit.short.' + kind), name: t('prodUnit.name.' + kind), dim };
}

/** "square feet (area, not pieces)" — the name, and what it measures. */
export function unitPhrase(unit, t) {
  const w = unitWords(unit, t);
  return w.dim ? t('prodUnit.withDim', { name: w.name, dim: t('prodUnit.dim.' + w.dim) }) : w.name;
}

/** Quantities → { n, max, median } over the days that reported any (> 0). */
export function dayStats(qtys) {
  const q = (qtys || []).map(Number).filter(v => v > 0).sort((a, b) => a - b);
  const n = q.length;
  if (!n) return { n: 0, max: 0, median: 0 };
  const median = n % 2 ? q[(n - 1) / 2] : (q[n / 2 - 1] + q[n / 2]) / 2;
  return { n, max: q[n - 1], median };
}

/**
 * 'max' when qty beats the highest day on file, 'median' when it is above
 * MEDIAN_FACTOR × the usual day (that one still catches a unit misread after
 * an earlier mistake has raised the max), null otherwise or with too little
 * history to judge.
 */
export function outlierOf(qty, stat) {
  const q = Number(qty);
  if (!(q > 0) || !stat || !(stat.n >= MIN_DAYS)) return null;
  if (q > stat.max) return 'max';
  if (stat.median > 0 && q > MEDIAN_FACTOR * stat.median) return 'median';
  return null;
}

const round1 = n => String(Math.round(Number(n) * 10) / 10);

/**
 * The field's question, one day: "364 sq ft in one day is more than any day
 * your company has reported for Veneer Brick (max 342). Did you count pieces
 * instead of square feet?" `scope` is 'company' or 'own' — whose history the
 * device could read.
 */
export function outlierMsg({ qty, item, unit, stat, why, scope }, t) {
  const w = unitWords(unit, t);
  const vars = { qty: round1(qty), unit: w.short, item, max: round1(stat.max), median: round1(stat.median),
    factor: MEDIAN_FACTOR };
  const key = why === 'median' ? 'prodOutlier.median' : scope === 'own' ? 'prodOutlier.maxOwn' : 'prodOutlier.max';
  const ask = w.dim ? t('prodOutlier.askMeasured', { name: w.name }) : t('prodOutlier.askCounted');
  return t(key, vars) + ' ' + ask;
}
